import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import React, { FC } from 'react';
import { gql, useQuery } from 'urql';

import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
  Value,
} from '@/components';
import { InformationIcon, RefreshIcon } from '@/components/icons';
import { useIsEnabledForOperator, useTranslate } from '@/contexts';
import {
  complianceLimitsLabels,
  getPeriodLabel,
} from '@/utils/compliance-limits';
import {
  PlayerComplianceGlobalLimitsQuery,
  PlayerComplianceGlobalLimitsQueryVariables,
} from './__generated__/components';
import LimitsDetails from './LimitsDetails';

export const Fragment = graphql`
  fragment SanityPlayerComplianceLimitsBlock on SanityPlayerComplianceLimitsBlock {
    title {
      ...SanityLocaleString
    }
    enabledByOperator {
      ...SanityOperator
    }
  }
`;

const query = gql`
  query PlayerComplianceGlobalLimits($playerId: ID!) {
    player(playerId: $playerId) {
      id
      complianceGlobalPlayerLimits {
        limitName
        activeFrom
        euroThreshold
        euroAmount
        limitReached
        latestChangeAt
        rollingPeriod {
          period
          periodType
        }
      }
    }
  }
`;

type LimitsValueProps = {
  props: PlayerComplianceGlobalLimitsQuery['player']['complianceGlobalPlayerLimits'][number];
  fetching?: boolean;
};

const LimitsValue: FC<LimitsValueProps> = ({ props }) => {
  const { limitName } = props;
  const limitLabel = `${complianceLimitsLabels[limitName] || limitName} - ${getPeriodLabel(props.rollingPeriod)}`;

  return (
    <Value
      title={
        <div className="flex">
          <span>{limitLabel}</span>
          <ControlledModal content={<LimitsDetails {...props} />}>
            <InlineIconButton>
              <InformationIcon />
            </InlineIconButton>
          </ControlledModal>
        </div>
      }
      value={props.limitReached ? 'Yes' : 'No'}
    />
  );
};

const PlayerScreeningStatusesBlock: FC<{
  block: Queries.SanityPlayerScreeningStatusesBlockFragment;
}> = ({ block }) => {
  const { playerId } = useParams();
  const { t } = useTranslate();
  const { isEnabled } = useIsEnabledForOperator(block.enabledByOperator);

  const [{ data, fetching }, refresh] = useQuery<
    PlayerComplianceGlobalLimitsQuery,
    PlayerComplianceGlobalLimitsQueryVariables
  >({
    query,
    variables: {
      playerId,
    },
    pause: !isEnabled,
  });

  if (!isEnabled) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="grid gap-3 p-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {data?.player.complianceGlobalPlayerLimits.map((limitsDetails) => (
            <LimitsValue
              key={limitsDetails.limitName}
              props={limitsDetails}
              fetching={fetching}
            />
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerScreeningStatusesBlock;
